import React, { useState, useEffect, useContext, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, Button, FormGroup, Label, Input, Form } from 'reactstrap';
import AdministracionPlanillaService from '../../axios_services/administracion_planilla.service';
import ArchivosService from '../../axios_services/archivos.service';
import Loader from '../Loader/Loader';
import { AppContext } from '../../application/provider';

const formatSoles = (amount) =>
    new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(amount || 0);

const calcTotals = (f, pct = 0.1137) => {
    const total_pagar = (parseFloat(f.sueldo_perfil) || 0)
        + (parseFloat(f.bono_anual) || 0)
        + (parseFloat(f.bono_gestion) || 0)
        + (parseFloat(f.vacaciones_truncas) || 0)
        - (parseFloat(f.descuento) || 0);
    const total_aporte = parseFloat((total_pagar * pct).toFixed(2));
    const importe_neto = parseFloat((total_pagar - total_aporte - (parseFloat(f.retencion) || 0)).toFixed(2));
    return { total_pagar_planilla: parseFloat(total_pagar.toFixed(2)), total_aporte, importe_neto };
};

const ModalActualizarPagoAdministracion = ({ isOpen, onClose, onRefresh, item, periodo }) => {
    const [state] = useContext(AppContext);
    const [isLoading, setIsLoading] = useState(false);

    const tipo_pagos = state.catalogos?.tipos?.filter(t => t.categoria_id == 32) || [];

    const [formData, setFormData] = useState({
        nombre_completo_usuario: '',
        perfil_descripcion: '',
        sueldo_perfil: 0,
        bono_anual: 0,
        bono_gestion: 0,
        vacaciones_truncas: 0,
        descuento: 0,
        total_aporte: 0,
        retencion: 0,
        importe_neto: 0,
        estado_pago_id: '',
        fecha_pago: ''
    });

    const [boletaFile, setBoletaFile] = useState(null);
    const fileRef = useRef(null);
    const [porcentaje, setPorcentaje] = useState(0.1137);

    const userProfile = Number(state.user?.perfil_id);
    const canUploadBoleta = [8, 5, 26, 22].includes(userProfile);
    const isPending = !item || !item.aprobado_th || Number(item.aprobado_th) !== 1;

    useEffect(() => {
        if (isOpen && item) {
            const perfil = Number(item.perfil_id);
            const pct = perfil === 1 ? 0.1292 : perfil === 9 ? 0.13 : 0.1137;
            setPorcentaje(pct);

            setFormData({
                nombre_completo_usuario: item.nombre_completo_usuario || '',
                perfil_descripcion: item.perfil_descripcion || '',
                sueldo_perfil: item.sueldo_perfil || 0,
                bono_anual: item.bono_anual || 0,
                bono_gestion: item.bono_gestion || 0,
                vacaciones_truncas: item.vacaciones_truncas || 0,
                descuento: item.descuento || 0,
                total_aporte: item.total_aporte || 0,
                retencion: item.retencion || 0,
                importe_neto: item.importe_neto || 0,
                estado_pago_id: item.estado_pago_id ? String(item.estado_pago_id) : '',
                fecha_pago: item.fecha_pago || ''
            });
            setBoletaFile(null);
            if (fileRef.current) fileRef.current.value = '';
        }
    }, [isOpen, item]);

    if (!item) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (['sueldo_perfil', 'bono_anual', 'bono_gestion', 'vacaciones_truncas', 'descuento', 'retencion'].includes(name)) {
            const val = parseFloat(value) || 0;
            setFormData(prev => {
                const updated = { ...prev, [name]: val };
                const totals = calcTotals(updated, porcentaje);
                return { ...updated, ...totals };
            });
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleActualizar = async () => {
        setIsLoading(true);
        try {
            const payload = {
                planilla_id: item.planilla_id,
            };

            if (isPending) {
                Object.assign(payload, {
                    nombre_completo_usuario: formData.nombre_completo_usuario,
                    perfil_descripcion: formData.perfil_descripcion,
                    sueldo_perfil: formData.sueldo_perfil,
                    bono_anual: formData.bono_anual,
                    bono_gestion: formData.bono_gestion,
                    vacaciones_truncas: formData.vacaciones_truncas,
                    descuento: formData.descuento,
                    total_aporte: formData.total_aporte,
                    retencion: formData.retencion,
                    importe_neto: formData.importe_neto
                });
            } else {
                Object.assign(payload, {
                    estado_pago_id: formData.estado_pago_id || null,
                    fecha_pago: formData.fecha_pago || null,
                });
            }

            await AdministracionPlanillaService.actualizarPago(payload);

            if (boletaFile && canUploadBoleta && !isPending) {
                try {
                    await ArchivosService.guardarBoletaPago(boletaFile, item.usuario_id, periodo, state.user.usuario_id);
                } catch (uploadError) {
                    const errorMsg = uploadError.response?.data?.message || '';
                    if (errorMsg.includes('failed to upload')) {
                        throw new Error('El archivo PDF pesa demasiado. Por favor, reduce el tamaño del PDF (límite 2MB) o verifica tu archivo.');
                    }
                    throw uploadError;
                }
            }

            alert('Datos actualizados correctamente.');
            if (onRefresh) onRefresh();
            onClose();
        } catch (e) {
            console.error(e);
            alert(e.message || 'Error al actualizar el pago. Inténtelo nuevamente.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} className="custom-modal" backdrop="static" size="md" centered>
            <ModalHeader className="modal-header">
                <div className="titulo">
                    <h1>ACTUALIZAR REGISTRO</h1>
                    <button className="close-btn" onClick={onClose}>X</button>
                </div>
            </ModalHeader>

            <div className="modal-body-footer">
                <ModalBody className="modal-body p-4">
                    {isLoading && <Loader />}

                    <Form onSubmit={(e) => { e.preventDefault(); handleActualizar(); }} className="px-4">
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Apellidos y Nombre:</Label>
                            <Input type="text" name="nombre_completo_usuario" value={formData.nombre_completo_usuario} onChange={handleChange} disabled={!isPending} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Cargo / Perfil:</Label>
                            <Input type="text" name="perfil_descripcion" value={formData.perfil_descripcion} onChange={handleChange} disabled={!isPending} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Remuneración (S/):</Label>
                            <Input type="number" step="0.01" name="sueldo_perfil" value={formData.sueldo_perfil} onChange={handleChange} disabled={!isPending} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Bono Anual (S/):</Label>
                            <Input type="number" step="0.01" name="bono_anual" value={formData.bono_anual} onChange={handleChange} disabled={!isPending} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Bono Gestión (S/):</Label>
                            <Input type="number" step="0.01" name="bono_gestion" value={formData.bono_gestion} onChange={handleChange} disabled={!isPending} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Vacaciones Truncas (S/):</Label>
                            <Input type="number" step="0.01" name="vacaciones_truncas" value={formData.vacaciones_truncas} onChange={handleChange} disabled={!isPending} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Descuento (S/):</Label>
                            <Input type="number" step="0.01" name="descuento" value={formData.descuento} onChange={handleChange} disabled={!isPending} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Aporte Trabajador (AFP/ONP):</Label>
                            <Input type="text" value={formatSoles(formData.total_aporte)} disabled className="fw-bold" />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Retención 5ta:</Label>
                            <Input type="number" step="0.01" name="retencion" value={formData.retencion} onChange={handleChange} disabled={!isPending} />
                        </FormGroup>
                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Importe Neto a Pagar:</Label>
                            <Input type="text" value={formatSoles(formData.importe_neto)} disabled className="fw-bold text-success" />
                        </FormGroup>

                        <hr />

                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Estado de Pago:</Label>
                            <Input
                                type="select"
                                name="estado_pago_id"
                                value={formData.estado_pago_id}
                                onChange={handleChange}
                                disabled={isPending}
                            >
                                <option value="">Seleccione un estado...</option>
                                {tipo_pagos.map(t => (
                                    <option key={t.tipo_id} value={t.tipo_id}>{t.descripcion}</option>
                                ))}
                            </Input>
                        </FormGroup>

                        <FormGroup className="mb-3 d-flex flex-column">
                            <Label className="d-block mb-1">Fecha de Pago:</Label>
                            <Input
                                type="date"
                                name="fecha_pago"
                                value={formData.fecha_pago}
                                onChange={handleChange}
                                disabled={isPending}
                            />
                        </FormGroup>

                        {canUploadBoleta && (
                            <FormGroup className="mb-4">
                                <Label>Subir/Actualizar Boleta de Pago (PDF):</Label>
                                <div className={`cap-upload-zone ${isPending ? 'opacity-50 pointer-events-none' : ''}`}>
                                    <span
                                        className="cap-upload-btn"
                                        onClick={() => !isPending && fileRef.current?.click()}
                                    >
                                        {boletaFile ? boletaFile.name : 'Seleccionar archivo (PDF)'}
                                    </span>
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept=".pdf"
                                        style={{ display: 'none' }}
                                        onChange={(e) => setBoletaFile(e.target.files[0] || null)}
                                        disabled={isPending}
                                    />
                                </div>
                                {boletaFile && (
                                    <small>
                                        <span
                                            style={{ cursor: 'pointer', color: '#f87171', fontSize: '10px' }}
                                            onClick={() => { setBoletaFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                                        >
                                            ✕ Quitar selección
                                        </span>
                                    </small>
                                )}
                                {item.boleta_url && !boletaFile && (
                                    <small className="text-success mt-1 d-block">
                                        ✓ Ya existe una boleta subida.{' '}
                                        <span
                                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                            onClick={() => window.open(item.boleta_url, '_blank')}
                                        >
                                            Ver
                                        </span>
                                    </small>
                                )}
                            </FormGroup>
                        )}

                        <div className="flex justify-end mt-4 pb-3">
                            <Button
                                className="btn-primary px-5 py-2"
                                onClick={handleActualizar}
                                style={{ borderRadius: '8px' }}
                                disabled={isLoading}
                            >
                                ACTUALIZAR
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </div>
        </Modal>
    );
};

export default ModalActualizarPagoAdministracion;
